import React from 'react';
import { Helmet } from 'react-helmet';
import Footer from './Footer';
import Nav from './Nav';

function Privacy() {
  return (
    <>
      <Nav />
      <Helmet>
        <title>Privacy Policy | Speak Your Menu</title>
        <meta
          name="description"
          content="Read the privacy policy for Speak Your Menu, detailing how we collect, use, and protect your information."
        />
        <link rel="canonical" href="https://speakyourmenu.com/privacy" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: "https://speakyourmenu.com/" },
            { "@type": "ListItem", position: 2, name: "Privacy Policy", item: "https://speakyourmenu.com/privacy" },
          ],
        })}</script>
      </Helmet>
      <main id="main-content" tabIndex="-1" className="container my-5">
        <h1 className="mb-4">Speak Your Menu Privacy Policy</h1>
        <p className="text-muted">Effective Date: September 16, 2025</p>

        {/* 1. Information We Collect */}
        <section>
          <h2>1. Information We Collect</h2>
          <h3>1.1 Account Information</h3>
          <p>When an owner, manager, director, or employee account is created we collect:</p>
          <ul className="ms-3">
            <li>uuid (system-generated)</li>
            <li>first &amp; last name</li>
            <li>business e-mail address (required, unique)</li>
            <li>hashed password</li>
            <li>optional profile image (avatar URL)</li>
            <li>role (super_admin, director, manager, employee)</li>
            <li>assigned_restaurants (array of restaurant UUIDs)</li>
            <li>permissions &amp; lessons_required flags</li>
            <li>assignedLessons, lesson_progress &amp; badges</li>
            <li>account status (active flag) and last_login timestamp</li>
          </ul>

          <h3>1.2 Restaurant &amp; Menu Data</h3>
          <p>Managers upload menu items, wine SKUs, ingredients, and food images so the platform can generate training lessons.</p>

          <h3>1.3 Employee Lesson Metrics</h3>
          <p>For each employee we store lesson assignment, completion status, scores, badges, and timestamps. No HR, payroll, or phone data is collected.</p>

          <h3>1.4 Operational Logs</h3>
          <p>Our Log collection records user UUID, action type, context details, role, restaurant UUID, and timestamp. We do not log IP addresses, browser/OS, device type, or geolocation.</p>

          <h3>1.5 Website Analytics and Cookie Preferences</h3>
          <p>
            We use analytics technologies on our public marketing site, including
            Google Analytics 4, to understand website usage, measure marketing
            performance, and improve how visitors find and use Speak Your Menu.
            These technologies may collect information such as page views, browser
            information, approximate location derived from IP address, referral
            source, and interaction events.
          </p>
          <p>
            In the European Economic Area (including Ireland, Iceland, Liechtenstein, and Norway),
            the United Kingdom, and Switzerland, we use Basic Consent Mode: Google Analytics
            does not load or send analytics requests unless you accept. If your country cannot be
            determined, we also ask first. Elsewhere, analytics may be enabled by default where
            permitted, without a global cookie banner.
          </p>
          <p>
            All visitors can use the Privacy/Cookie Settings control at the bottom of every page
            to accept, reject, or withdraw analytics consent. The website and its forms remain
            available regardless of your choice. We store one first-party preference record in
            your browser for 180 days; your explicit choice takes precedence over your location,
            including when you travel. Changes apply to other open tabs on this site. Withdrawing
            consent stops optional tracking, removes accessible Google Analytics cookies on this
            site, and reloads the page to stop the running analytics tag. It does not erase data
            already sent to Google. Browser storage restrictions can prevent us from remembering
            your choice; we display an error if that happens.
          </p>
          <p>
            We do not use Meta Pixel. Advertising consent is always denied, Google Signals and
            advertising personalization are disabled, and we retain approximate country reporting
            for permitted analytics. We sanitize page URLs to exclude unapproved query parameters
            and sensitive values before sending our page views and conversion events.
          </p>
          <p>
            We do not use these tools to power employee lesson scoring or restaurant training operations
            inside the product itself. If our use of tracking technologies changes materially, we will
            update this Policy before or at the time the change takes effect.
          </p>

          <h3>1.6 Assessment Responses</h3>
          <p>
            When you complete a Speak Your Menu assessment, we collect your responses,
            baseline scores, and the business e-mail address you provide in order to
            generate your Results and deliver them to you. These responses may include
            operational details about your restaurant’s training practices. We use this
            data only to calculate your Results, to improve our assessment methodology,
            and to provide you with a one-time results e-mail (unless you separately opt
            in to marketing communications).
          </p>
        </section>

        {/* 2. How We Use Information */}
        <section>
          <h2>2. How We Use Information</h2>
          <ul className="ms-3">
            <li>Operate &amp; deliver the Services and personalise the training experience.</li>
            <li>Improve &amp; develop new features, analytics dashboards, and lesson algorithms.</li>
            <li>Communicate with account owners and managers about updates, security alerts, and service messages.</li>
            <li>Marketing emails to owners and managers (never to employees) – each message includes an unsubscribe link.</li>
            <li>Generate and deliver assessment Results, provide follow-up insights, and improve our lift-calculation models.</li>
            <li>
              With your consent, send ongoing marketing communications (e.g., product tips,
              promotions, research updates) after you complete an assessment. You may withdraw
              consent at any time by clicking “unsubscribe” in any marketing email.
            </li>
            <li>Research &amp; insights – we may aggregate and anonymise usage data to create industry reports.</li>
          </ul>
        </section>

        {/* 3. Legal Bases (GDPR) */}
        <section>
          <h2>3. Legal Bases (GDPR)</h2>
          <p>Where the GDPR applies we process personal data under these bases:</p>
          <ul className="ms-3">
            <li>Performance of a contract (providing the platform)</li>
            <li>Legitimate interests (security, product improvement)</li>
            <li>Consent (marketing emails, including ongoing communications after assessments)</li>
          </ul>
        </section>

        {/* 4. Sharing & Sub-processors */}
        <section>
          <h2>4. Sharing &amp; Sub-processors</h2>
          <p>We share data only with the service providers necessary to run the platform:</p>
          <table className="table table-bordered">
            <thead>
              <tr>
                <th>Provider</th>
                <th>Purpose</th>
                <th>Location</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Amazon Web Services (AWS)</td>
                <td>EC2 hosting, S3 encrypted backups</td>
                <td>USA (us-east-1)</td>
              </tr>
              <tr>
                <td>MongoDB Atlas</td>
                <td>Managed database clusters</td>
                <td>USA (us-east-1)</td>
              </tr>
              <tr>
                <td>Stripe</td>
                <td>Subscription billing</td>
                <td>USA</td>
              </tr>
              <tr>
                <td>Google (Gmail SMTP)</td>
                <td>Transactional &amp; onboarding email (via NodeMailer)</td>
                <td>USA</td>
              </tr>
              <tr>
                <td>MailerLite</td>
                <td>Marketing email campaigns</td>
                <td>EU / USA</td>
              </tr>
            </tbody>
          </table>
          <p>
            We require each Sub-processor to sign data-processing agreements with protections
            equal to this Policy. An up-to-date list is maintained at{" "}
            <a
              href="https://speakyourmenu.com/subprocessors"
              target="_blank"
              rel="noopener noreferrer"
            >
              our Subprocessors page
            </a>.
          </p>
        </section>

        {/* 5. International Transfers */}
        <section>
          <h2>5. International Transfers</h2>
          <p>
            The primary data centre is in the United States (AWS us-east-1). Authorised
            personnel may access production data from outside the U.S. (e.g., a freelance
            engineer in Pakistan) under strict role-based access controls and confidentiality
            agreements. We rely on Standard Contractual Clauses and equivalent safeguards
            for such transfers.
          </p>
        </section>

        {/* 6. Data Retention */}
        <section>
          <h2>6. Data Retention</h2>
          <table className="table table-bordered">
            <thead>
              <tr>
                <th>Data category</th>
                <th>Retention period</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Active account data</td>
                <td>Stored until the customer deletes the data or cancels the subscription</td>
              </tr>
              <tr>
                <td>Menu &amp; lesson data after cancellation</td>
                <td>90 days (customer grace period)</td>
              </tr>
              <tr>
                <td>Encrypted daily backups</td>
                <td>30 days rolling window, then automatically deleted</td>
              </tr>
              <tr>
                <td>Operational &amp; security logs</td>
                <td>180 days, then de-identified or deleted</td>
              </tr>
              <tr>
                <td>Aggregate / anonymised analytics</td>
                <td>Retained indefinitely (no personal data)</td>
              </tr>
              <tr>
                <td>Assessment responses &amp; results</td>
                <td>
                  Retained as long as necessary to provide the Services and improve our models
                </td>
              </tr>
            </tbody>
          </table>
          <p>You may request early deletion at any time (see § 7).</p>
        </section>

        {/* 7. Your Rights & Choices */}
        <section>
          <h2>7. Your Rights &amp; Choices</h2>
          <ul className="ms-3">
            <li>
              Access / deletion / correction: You may email{" "}
              <a href="mailto:support@speakyourmenu.com">support@speakyourmenu.com</a> to
              request a copy, deletion, or correction of your personal data. We respond
              within 30 days.
            </li>
            <li>
              Marketing opt-out: Every marketing email contains an unsubscribe link that
              instantly removes you from future campaigns.
            </li>
            <li>
              California “Do Not Sell/Share” (future): We currently sell only aggregated,
              de-identified statistics. If we begin selling identifiable restaurant data we
              will add a “Do Not Sell or Share My Personal Information” link and update this
              Policy 30 days in advance.
            </li>
          </ul>
        </section>

        {/* 8. Security */}
        <section>
          <h2>8. Security</h2>
          <ul className="ms-3">
            <li>TLS 1.2/1.3 encryption in transit &amp; AES-256 at rest</li>
            <li>Least-privilege IAM and MFA required for all manager and admin logins</li>
            <li>Annual third-party penetration testing and continuous vulnerability scanning</li>
            <li>Encrypted backups with tight S3 bucket policies</li>
            <li>Logged access to production systems and rapid incident-response procedures</li>
          </ul>
        </section>

        {/* 9. Children’s Privacy */}
        <section>
          <h2>9. Children’s Privacy</h2>
          <p>
            The Services are not intended for individuals under 16 years of age. We do not
            knowingly collect personal data from children. If you believe a child under 16
            has provided us data, please contact us and we will delete it promptly.
          </p>
        </section>

        {/* 10. Changes to This Policy */}
        <section>
          <h2>10. Changes to This Policy</h2>
          <p>
            We may update this Privacy Policy from time to time. Changes will be posted at{" "}
            <a
              href="https://speakyourmenu.com/privacy"
              target="_blank"
              rel="noopener noreferrer"
            >
              https://speakyourmenu.com/privacy
            </a>{" "}
            with a revised “Effective Date.” If changes are material, we will provide 30
            days’ notice via e-mail or in-app banner.
          </p>
        </section>

        {/* 11. Contact Us */}
        <section>
          <h2>11. Contact Us</h2>
          <address>
            <strong>Speak Your Menu, Inc.</strong>
            <br />
            Attn: Privacy Officer
            <br />
            89 Vanderbilt Ave, Apt. 1
            <br />
            Brooklyn, NY 11205 USA
            <br />
            Email:{" "}
            <a href="mailto:support@speakyourmenu.com">support@speakyourmenu.com</a>
          </address>

          <p className="mt-4">
            By using our Services you acknowledge that you have read and understood this
            Privacy Policy.
          </p>
        </section>
      </main>
      <Footer />
    </>
  );
}

export default Privacy;
